<template>
  <div ref="turnstileEl" class="turnstile-widget"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';

interface Props {
  sitekey: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
}

interface Emits {
  (e: 'token', token: string): void;
  (e: 'error'): void;
  (e: 'expired'): void;
}

const props = withDefaults(defineProps<Props>(), {
  theme: 'auto',
  size: 'normal'
});

const emit = defineEmits<Emits>();

const turnstileEl = ref<HTMLElement>();
const widgetId = ref<string>();

const waitForTurnstile = (): Promise<void> => {
  return new Promise((resolve) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const checkInterval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(checkInterval);
        resolve();
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.turnstile) {
        console.error('Turnstile failed to load');
        emit('error');
      }
    }, 10000);
  });
};

onMounted(async () => {
  await waitForTurnstile();

  if (turnstileEl.value && window.turnstile) {
    try {
      widgetId.value = window.turnstile.render(turnstileEl.value, {
        sitekey: props.sitekey,
        theme: props.theme,
        size: props.size,
        callback: (token: string) => {
          emit('token', token);
        },
        'error-callback': () => {
          emit('error');
        },
        'expired-callback': () => {
          emit('expired');
        }
      });
    } catch (error) {
      console.error('Failed to render Turnstile widget:', error);
      emit('error');
    }
  }
});

onBeforeUnmount(() => {
  if (widgetId.value && window.turnstile) {
    try {
      window.turnstile.remove(widgetId.value);
    } catch (error) {
      console.error('Failed to remove Turnstile widget:', error);
    }
  }
});

defineExpose({
  reset: () => {
    if (widgetId.value && window.turnstile) {
      window.turnstile.reset(widgetId.value);
    }
  }
});
</script>

<style scoped>
.turnstile-widget {
  display: flex;
  justify-content: center;
  margin: 1rem 0;
}
</style>
